import { canBook } from '../availability';
import { bookInTx } from '../booking';
import { normalizeIsraeliPhone } from '../phone';
import { depositRule, enabledOnlineMethods, holdExpiry } from '../policies';
import { HOUR, dayKeyOf, toMs } from '../time';
import {
  fail, newId, ok, type Appointment, type Client, type DayKey, type Daypart, type Id, type IsoUtc, type Message, type Result, type Service,
  type WaitlistEntry, type WaitlistOffer,
} from '../types';
import { decideAccept, entryIsStale, matchWaitlist, offerExpiry, slotKeyOf } from '../waitlist';
import { BOOKING_STORES } from '../../adapters/types';
import { clientUseCases } from './clients';
import { availabilityData, firstName, listAppointments, loadBundle, loadBundleBySlug, loadPublicBundle, makeToken, notifyAppointment, planText, sendDirect, type Bundle, type Deps } from './context';
import type { OpenedSlot } from './booking';

export interface JoinWaitlistInput {
  slug?: string;
  businessId?: Id;
  serviceId: Id;
  staffId?: Id;
  client: { name: string; phone: string };
  days: DayKey[];
  daypart: Daypart;
  /** The reminders/updates checkbox; offers cannot be sent without it. */
  consentReminders: boolean;
}
export interface OfferView {
  offer: WaitlistOffer;
  state: 'open' | 'taken' | 'expired' | 'accepted';
  service: Service;
  businessName: string;
  clientFirstName: string;
  manageUrl?: string;
}
export type OpenedSlotCard = OpenedSlot & { waitingCount: number; canOffer: boolean };

export function waitlistUseCases(d: Deps) {
  const clients = clientUseCases(d);
  const now = (): IsoUtc => d.clock.nowIso();

  async function joinWaitlist(input: JoinWaitlistInput): Promise<Result<{ entry: WaitlistEntry; client: Client }, 'business_not_found' | 'service_not_found' | 'invalid_phone' | 'invalid_name' | 'no_days' | 'consent_required'>> {
    const b = input.businessId ? await loadPublicBundle(d, input.businessId) : input.slug ? await loadBundleBySlug(d, input.slug) : undefined;
    if (!b) return fail('business_not_found');
    if (!b.services.some((s) => s.id === input.serviceId && s.active)) return fail('service_not_found');
    const phone = normalizeIsraeliPhone(input.client.phone);
    if (!phone.ok || phone.kind !== 'mobile') return fail('invalid_phone');
    if (input.client.name.trim().length < 2) return fail('invalid_name');
    const today = dayKeyOf(d.clock.now(), b.business.tz);
    const days = [...new Set(input.days)].filter((x) => x >= today).sort();
    if (days.length === 0) return fail('no_days');
    if (!input.consentReminders) return fail('consent_required');

    const { client } = await clients.findOrCreate(b.business.id, input.client.name, phone.e164);
    await clients.recordConsent({ clientId: client.id, purpose: 'reminders', source: 'waitlist' });
    const [existing] = await d.storage.repo('waitlistEntries').list({ where: { clientId: client.id }, filter: (e) => e.serviceId === input.serviceId && e.status === 'waiting' });
    const t = now();
    const entry: WaitlistEntry = existing
      ? { ...existing, days: [...new Set([...existing.days, ...days])].sort(), daypart: input.daypart, ...(input.staffId ? { staffId: input.staffId } : {}), updatedAt: t }
      : {
        id: newId(d.clock.now()), businessId: b.business.id, createdAt: t, updatedAt: t, clientId: client.id, serviceId: input.serviceId,
        ...(input.staffId ? { staffId: input.staffId } : {}), days, daypart: input.daypart, status: 'waiting',
      };
    await d.storage.repo('waitlistEntries').put(entry);
    return ok({ entry, client });
  }

  async function leaveWaitlist(entryId: Id): Promise<void> {
    const e = await d.storage.repo('waitlistEntries').get(entryId);
    if (e && (e.status === 'waiting' || e.status === 'offered')) await d.storage.repo('waitlistEntries').put({ ...e, status: 'expired', updatedAt: now() });
  }

  async function listWaitlist(businessId: Id): Promise<Array<{ entry: WaitlistEntry; client: Client | undefined; service: Service | undefined }>> {
    const entries = (await d.storage.repo('waitlistEntries').list({ where: { businessId } })).filter((e) => e.status === 'waiting' || e.status === 'offered');
    const out = [];
    for (const entry of entries.sort((a, c) => a.createdAt.localeCompare(c.createdAt))) {
      out.push({ entry, client: await d.storage.repo('clients').get(entry.clientId), service: (await d.storage.repo('services').get(entry.serviceId)) });
    }
    return out;
  }

  /** Waiting entries that could take this slot right now (service fits, staff free, rules satisfied). */
  async function eligibleEntries(b: Bundle, appointments: Appointment[], slot: { start: IsoUtc; staffId: Id }) {
    const entries = await d.storage.repo('waitlistEntries').list({ where: { businessId: b.business.id, status: 'waiting' } });
    const data = availabilityData(b, appointments);
    return matchWaitlist(entries, { ...slot, end: slot.start }, b.business.tz).flatMap((entry) => {
      const service = b.services.find((s) => s.id === entry.serviceId);
      if (!service) return [];
      const check = canBook({ ...data, now: d.clock.now() }, service, { serviceId: service.id, staffId: slot.staffId, start: slot.start, source: 'waitlist' });
      return check.ok ? [{ entry, service, end: check.end }] : [];
    });
  }

  /** Today-view cards: freed future slots with waiting clients and no live offer yet. */
  async function openedSlotCards(b: Bundle, appointments: Appointment[]): Promise<OpenedSlotCard[]> {
    const nowMs = d.clock.now();
    const offers = await d.storage.repo('waitlistOffers').list({ where: { businessId: b.business.id } });
    const cards: OpenedSlotCard[] = [];
    const seen = new Set<string>();
    const recent = nowMs - 24 * HOUR;
    for (const a of appointments.filter((x) => (x.status === 'cancelled_client' || x.status === 'cancelled_owner') && toMs(x.start) > nowMs && x.cancelledAt !== undefined && toMs(x.cancelledAt) >= recent)) {
      const key = slotKeyOf(a.staffId, a.start);
      if (seen.has(key) || offers.some((o) => o.slotKey === key && o.status === 'open')) continue;
      seen.add(key);
      const matches = await eligibleEntries(b, appointments, { start: a.start, staffId: a.staffId });
      if (matches.length > 0) cards.push({ start: a.start, end: a.end, staffId: a.staffId, serviceId: a.serviceId, waitingCount: matches.length, canOffer: offerExpiry(nowMs, toMs(a.start)) !== undefined });
    }
    return cards.sort((x, y) => x.start.localeCompare(y.start));
  }

  /** One tap: sends an offer to every waiting client who fits. First accept wins. */
  async function offerSlotToWaitlist(input: { businessId: Id; slotStart: IsoUtc; staffId: Id }): Promise<Result<{ offers: WaitlistOffer[]; expiresAt: IsoUtc; messages: Message[] }, 'business_not_found' | 'too_late' | 'no_matches'>> {
    const b = await loadBundle(d, input.businessId);
    if (!b) return fail('business_not_found');
    const expiresAt = offerExpiry(d.clock.now(), toMs(input.slotStart));
    if (!expiresAt) return fail('too_late');
    const appointments = await listAppointments(d, b.business.id);
    const slotKey = slotKeyOf(input.staffId, input.slotStart);
    const live = (await d.storage.repo('waitlistOffers').list({ where: { slotKey } })).filter((o) => o.status === 'open');
    const matches = (await eligibleEntries(b, appointments, { start: input.slotStart, staffId: input.staffId })).filter((m) => !live.some((o) => o.entryId === m.entry.id));
    if (matches.length === 0) return fail('no_matches');

    const t = now();
    const offers: WaitlistOffer[] = [];
    const messages: Message[] = [];
    for (const m of matches) {
      const offer: WaitlistOffer = {
        id: newId(d.clock.now()), businessId: b.business.id, createdAt: t, updatedAt: t, entryId: m.entry.id, clientId: m.entry.clientId,
        serviceId: m.service.id, staffId: input.staffId, slotStart: input.slotStart, slotEnd: m.end, slotKey, token: makeToken(), expiresAt, status: 'open',
      };
      await d.storage.transact(['waitlistOffers', 'waitlistEntries'], async (tx) => {
        await tx.put('waitlistOffers', offer);
        await tx.put('waitlistEntries', { ...m.entry, status: 'offered', updatedAt: t });
      });
      offers.push(offer);
      const client = await d.storage.repo('clients').get(offer.clientId);
      if (!client) continue;
      const text = planText(d, b);
      const sent = await sendDirect(d, b, client, {
        template: 'waitlist_offer',
        params: {
          businessName: b.business.name, clientName: firstName(client.name), serviceName: m.service.name, date: text.date(toMs(offer.slotStart)),
          time: text.time(toMs(offer.slotStart)), offerUrl: d.links.offer(offer.token), expiresTime: text.time(toMs(expiresAt)),
        },
      }, `waitlist_offer:${offer.id}`);
      if (sent) messages.push(sent);
    }
    return ok({ offers, expiresAt, messages });
  }

  async function offerByToken(token: string): Promise<WaitlistOffer | undefined> {
    return token === '' ? undefined : (await d.storage.repo('waitlistOffers').list({ where: { token } }))[0];
  }

  async function getOfferView(token: string): Promise<Result<OfferView, 'not_found'>> {
    const offer = await offerByToken(token);
    const b = offer ? await loadBundle(d, offer.businessId) : undefined;
    const service = b?.services.find((s) => s.id === offer?.serviceId);
    if (!offer || !b || !service) return fail('not_found');
    const siblings = (await d.storage.repo('waitlistOffers').list({ where: { slotKey: offer.slotKey } })).filter((o) => o.id !== offer.id);
    const client = await d.storage.repo('clients').get(offer.clientId);
    const decision = decideAccept(offer, siblings, d.clock.now(), true);
    const state = decision === 'accept' ? 'open' : decision === 'already_accepted' ? 'accepted' : decision;
    const appt = offer.appointmentId ? await d.storage.repo('appointments').get(offer.appointmentId) : undefined;
    return ok({
      offer, state: state === 'invalid' ? 'expired' : state, service, businessName: b.business.name, clientFirstName: client ? firstName(client.name) : '',
      ...(appt ? { manageUrl: d.links.manage(appt.manageToken) } : {}),
    });
  }

  type AcceptOutcome = { kind: 'booked' | 'again'; appointment: Appointment } | { kind: 'fail'; reason: 'not_found' | 'taken' | 'expired' };

  /** First accept wins: decided and booked inside one storage transaction. */
  async function acceptOffer(token: string): Promise<Result<{ appointment: Appointment; manageUrl: string; payment?: { paymentId: Id; checkoutUrl: string } }, 'not_found' | 'taken' | 'expired'>> {
    const found = await offerByToken(token);
    if (!found) return fail('not_found');
    const b = await loadBundle(d, found.businessId);
    const service = b?.services.find((s) => s.id === found.serviceId);
    if (!b || !service) return fail('not_found');
    const deposit = depositRule(service, b.settings, b.plan.caps);
    const depositMethod = enabledOnlineMethods(b.settings)[0];
    const t = now();

    const outcome = await d.storage.transact([...BOOKING_STORES, 'waitlistOffers', 'waitlistEntries'], async (tx): Promise<AcceptOutcome> => {
      const offer = (await tx.list('waitlistOffers', { where: { token } }))[0];
      if (!offer) return { kind: 'fail', reason: 'not_found' };
      const siblings = (await tx.list('waitlistOffers', { where: { slotKey: offer.slotKey } })).filter((o) => o.id !== offer.id);
      const pre = decideAccept(offer, siblings, d.clock.now(), true);
      if (pre === 'already_accepted') {
        const a = offer.appointmentId ? await tx.get('appointments', offer.appointmentId) : undefined;
        return a ? { kind: 'again', appointment: a } : { kind: 'fail', reason: 'taken' };
      }
      if (pre !== 'accept') return { kind: 'fail', reason: pre === 'expired' ? 'expired' : 'taken' };
      const booked = await bookInTx(tx, {
        businessId: offer.businessId, clientId: offer.clientId, serviceId: offer.serviceId, staffId: offer.staffId, start: offer.slotStart, source: 'waitlist',
        status: deposit.required ? 'awaiting_deposit' : 'confirmed', manageToken: makeToken(), priceAgorot: service.priceAgorot,
        depositAgorot: deposit.amountAgorot, depositStatus: deposit.required ? 'required' : 'none',
        ...(deposit.required && depositMethod ? { depositMethod } : {}),
        ...(deposit.required ? { holdExpiresAt: holdExpiry(d.clock.now(), b.settings) } : {}),
      }, t);
      if (!booked.ok) {
        await tx.put('waitlistOffers', { ...offer, status: 'taken', updatedAt: t });
        return { kind: 'fail', reason: 'taken' };
      }
      await tx.put('waitlistOffers', { ...offer, status: 'accepted', appointmentId: booked.appointment.id, updatedAt: t });
      const entry = await tx.get('waitlistEntries', offer.entryId);
      if (entry) await tx.put('waitlistEntries', { ...entry, status: 'booked', appointmentId: booked.appointment.id, updatedAt: t });
      for (const s of siblings.filter((o) => o.status === 'open')) {
        await tx.put('waitlistOffers', { ...s, status: 'taken', updatedAt: t });
        const e = await tx.get('waitlistEntries', s.entryId);
        if (e && e.status === 'offered') await tx.put('waitlistEntries', { ...e, status: 'waiting', updatedAt: t });
      }
      return { kind: 'booked', appointment: booked.appointment };
    });

    if (outcome.kind === 'fail') return fail(outcome.reason);
    let appointment = outcome.appointment;
    let payment: { paymentId: Id; checkoutUrl: string } | undefined;
    if (outcome.kind === 'booked') {
      if (deposit.required && depositMethod !== 'card') {
        await notifyAppointment(d, 'deposit_requested', appointment, { payUrl: d.links.manage(appointment.manageToken) });
      } else if (deposit.required) {
        const created = await d.payments.createDeposit({ businessId: b.business.id, appointmentId: appointment.id, amountAgorot: deposit.amountAgorot, holdMinutes: b.settings.depositHoldMin });
        appointment = { ...appointment, paymentId: created.paymentId, updatedAt: now() };
        await d.storage.repo('appointments').put(appointment);
        payment = created;
        await notifyAppointment(d, 'deposit_requested', appointment, { payUrl: d.links.pay(created.paymentId) });
      } else {
        await notifyAppointment(d, 'booked', appointment);
      }
      await notifyTaken(b, service, found.slotKey, found.id);
    }
    return ok({ appointment, manageUrl: d.links.manage(appointment.manageToken), ...(payment ? { payment } : {}) });
  }

  /** Tells the other offered clients the slot is gone; they stay on the list. */
  async function notifyTaken(b: Bundle, service: Service, slotKey: string, winnerOfferId: Id): Promise<void> {
    const losers = (await d.storage.repo('waitlistOffers').list({ where: { slotKey } })).filter((o) => o.id !== winnerOfferId && o.status === 'taken');
    for (const o of losers) {
      const client = await d.storage.repo('clients').get(o.clientId);
      if (!client) continue;
      const text = planText(d, b);
      await sendDirect(d, b, client, {
        template: 'waitlist_offer_taken',
        params: { businessName: b.business.name, clientName: firstName(client.name), serviceName: service.name, date: text.date(toMs(o.slotStart)), time: text.time(toMs(o.slotStart)) },
      }, `waitlist_offer_taken:${o.id}`);
    }
  }

  async function declineOffer(token: string): Promise<Result<true, 'not_found'>> {
    const offer = await offerByToken(token);
    if (!offer) return fail('not_found');
    if (offer.status === 'open') {
      const t = now();
      await d.storage.repo('waitlistOffers').put({ ...offer, status: 'withdrawn', updatedAt: t });
      const e = await d.storage.repo('waitlistEntries').get(offer.entryId);
      if (e && e.status === 'offered') await d.storage.repo('waitlistEntries').put({ ...e, status: 'waiting', updatedAt: t });
    }
    return ok(true);
  }

  /** Scheduler step: runs out stale offers and drops entries whose days have all passed. */
  async function expireWaitlist(b: Bundle): Promise<number> {
    const nowMs = d.clock.now();
    const today = dayKeyOf(nowMs, b.business.tz);
    let n = 0;
    for (const o of await d.storage.repo('waitlistOffers').list({ where: { businessId: b.business.id, status: 'open' } })) {
      if (nowMs < toMs(o.expiresAt)) continue;
      const t = now();
      await d.storage.repo('waitlistOffers').put({ ...o, status: 'expired', updatedAt: t });
      const e = await d.storage.repo('waitlistEntries').get(o.entryId);
      if (e && e.status === 'offered') await d.storage.repo('waitlistEntries').put({ ...e, status: entryIsStale(e, today) ? 'expired' : 'waiting', updatedAt: t });
      n++;
    }
    for (const e of await d.storage.repo('waitlistEntries').list({ where: { businessId: b.business.id, status: 'waiting' } })) {
      if (entryIsStale(e, today)) await d.storage.repo('waitlistEntries').put({ ...e, status: 'expired', updatedAt: now() });
    }
    return n;
  }

  return { joinWaitlist, leaveWaitlist, listWaitlist, offerSlotToWaitlist, getOfferView, acceptOffer, declineOffer, openedSlotCards, expireWaitlist };
}
